import { noop } from 'lodash-es';
import {
  createContext,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import { CustomTabIcon } from 'src/components/CustomTabs/customTabIcons';
import { useCustomTabsContext } from 'src/components/CustomTabs/CustomTabsContext';
import { BoostIconDisplay } from 'src/components/SponsoredListings/BoostIconDisplay/BoostIconDisplay';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import {
  useEventHubContextEnabledSelector,
  useEventHubContextInventoryTabSelector,
  useEventHubContextIsOnSidePanelSelector,
  useEventHubContextOnUpdateInventoryTabSelector,
} from 'src/contexts/EventHubContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { usePosLiteAccount } from 'src/hooks/usePosLiteAccount';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  DashboardIcon,
  DeliveryIcon,
  FlashSaleIcon,
  ListingIcon,
  PlusIcon,
  PurchaseIcon,
  SellThroughRateIcon,
  TagNewIcon,
} from 'src/svgs';
import { SaleIcon } from 'src/svgs/SaleIcon';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { DemandDashboardIsOnSaleEventQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CompListing,
  Event,
  EventWithData,
  Feature,
  Listing,
  SellerEventMarketplaceEventInfo,
  UserSetting,
} from 'src/WebApiController';
import {
  createContext as createContextWithSelector,
  useContext as useContextWithSelector,
} from 'use-context-selector';

import { InventoryEventTabs } from '../InventoryEventTabs';
import { useMarketplaceEventInfo } from './InventoryEventLocalContext.hooks';

type EventContent = {
  id: InventoryEventTabs;
  Icon: React.ComponentType<Omit<PosIconProps, 'viewBox'>>;
  contentId: ContentId;
  labelOverride?: string;
  useStroke?: boolean;
  mobileOnly?: boolean;
  hide?: boolean;
};

export type IInventoryEventLocalContext = {
  defaultTab?: InventoryEventTabs;
  activeTab: InventoryEventTabs;
  eventContents: EventContent[];
  marketplaceEventInfo?: SellerEventMarketplaceEventInfo[];
  listings: Listing[];
  compListings: CompListing[];
  showMap: boolean;
  toggleShowMap: (showMap: boolean) => void;
  setActiveTab: (tab: InventoryEventTabs) => void;
  refetchMarketplaceEventInfo: () => void;
  onUpdateDefaultType: (type: InventoryEventTabs) => void;
};

const defaultContextValue: IInventoryEventLocalContext = {
  activeTab: InventoryEventTabs.Info,
  eventContents: [],
  listings: [],
  compListings: [],
  showMap: true,
  toggleShowMap: noop,
  setActiveTab: noop,
  refetchMarketplaceEventInfo: noop,
  onUpdateDefaultType: noop,
};

export const InventoryEventLocalContext =
  createContext<IInventoryEventLocalContext>(defaultContextValue);

export const InventoryEventLocalContextWithSelector =
  createContextWithSelector<IInventoryEventLocalContext>(defaultContextValue);

/**
 * @deprecated use the explicity context selectors to avoid unnecessary re-render
 */
export const useInventoryEventLocalContext = () => {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const context = useContext(InventoryEventLocalContext);
  const contextWithSelector = useContextWithSelector(
    InventoryEventLocalContextWithSelector
  );
  return hasContextWithSelectorFeature ? contextWithSelector : context;
};

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
const InventoryEventLocalContextProviderRaw = ({
  children,
  event,
  accordionItemProps,
}: PropsWithChildren & {
  event?: Event;
  accordionItemProps?: EventWithData;
}) => {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const hasEventDiscoveryInInventoryViewFeature = useUserHasFeature(
    Feature.EventDiscoveryInInventoryView
  );

  const isMobileLayout = useMatchMedia('mobile');
  const [searchParams] = useSearchParams();
  const isFromEventDiscovery = useMemo(
    () => Boolean(searchParams.get(DemandDashboardIsOnSaleEventQueryParam)),
    [searchParams]
  );

  const contentTypeInitRef = useRef<boolean>(false);

  const onUpdateInventoryTab = useEventHubContextOnUpdateInventoryTabSelector();
  const isOnSidePanel = useEventHubContextIsOnSidePanelSelector();
  const inventoryTab = useEventHubContextInventoryTabSelector();
  const enabled = useEventHubContextEnabledSelector();

  // Move the initial tab calculation into useState's lazy initialization
  const [activeTab, setActiveTab] = useState<InventoryEventTabs>(
    InventoryEventTabs.Info
  );

  // Used for mobile App
  const [showMap, toggleShowMap] = useToggle(true);

  const hasPricingV2DemandDashboard = useUserHasFeature(
    Feature.PricingV2DemandDashboard
  );
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const { isEventsEnabledForSponsoredListingsLoading } =
    useAdCampaignsDataContext();

  const isEventEnabledForBoost = useMemo(() => {
    if (event?.viagId == null) {
      return false;
    }
    return isEventsEnabledForSponsoredListingsLoading(event.viagId);
  }, [event?.viagId, isEventsEnabledForSponsoredListingsLoading]);

  const isPosLite = usePosLiteAccount();
  const hasCustomTabsFeature = useUserHasFeature(Feature.CustomTabs);
  const { tabs: allCustomTabs } = useCustomTabsContext();
  const customEventTabs = useMemo(
    () =>
      hasCustomTabsFeature
        ? allCustomTabs.filter((t) => t.scope === 'EventLevel')
        : [],
    [allCustomTabs, hasCustomTabsFeature]
  );
  const hasPricingABTestFeature = useUserHasFeature(Feature.PricingABTest);
  const hasFulfillmentRulesEventLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesEventLevel
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasMarketOrdersFeature = useUserHasFeature(Feature.MarketOrders);

  const { marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useMarketplaceEventInfo(event);

  const {
    value: defaultInventoryTabUserSetting,
    setUserSetting: setDefaultInventoryTabUserSetting,
  } = useServerUserSetting<InventoryEventTabs>({
    id: UserSetting.InventoryDefaultTab,
  });

  const listings = useMemo(
    () => accordionItemProps?.entities?.listings ?? [],
    [accordionItemProps?.entities?.listings]
  );
  const { compListingsQuery } = useGetCompListings(event);

  const onSetInventoryTab = useCallback(
    (tab: InventoryEventTabs) => {
      setActiveTab(tab);
      if (enabled && !isOnSidePanel) {
        onUpdateInventoryTab(tab);
      }
    },
    [enabled, isOnSidePanel, onUpdateInventoryTab]
  );

  useEffect(() => {
    if (isOnSidePanel && inventoryTab) {
      setActiveTab(inventoryTab);
    }
  }, [enabled, inventoryTab, isOnSidePanel]);

  useEffect(() => {
    if (isFromEventDiscovery) {
      setActiveTab(InventoryEventTabs.EventDiscovery);
      return;
    }
    if (
      !isMobileLayout &&
      defaultInventoryTabUserSetting &&
      contentTypeInitRef.current === false &&
      Object.values(InventoryEventTabs).includes(defaultInventoryTabUserSetting)
    ) {
      if (defaultInventoryTabUserSetting === InventoryEventTabs.None) {
        setActiveTab(InventoryEventTabs.Info);
      } else {
        setActiveTab(defaultInventoryTabUserSetting);
      }
      contentTypeInitRef.current = true;
    }
  }, [defaultInventoryTabUserSetting, isFromEventDiscovery, isMobileLayout]);

  const { isDarkMode } = useSiteTheme();

  const eventContents = useMemo(() => {
    const contents: EventContent[] = [
      {
        id: InventoryEventTabs.Info,
        Icon: DashboardIcon,
        contentId: ContentId.Inventory,
      },
    ];

    if (hasEventDiscoveryInInventoryViewFeature && !isMobileLayout) {
      contents.push({
        id: InventoryEventTabs.EventDiscovery,
        Icon: SearchSolidIcon,
        contentId: ContentId.EventDiscovery,
      });
    }

    if (!isPosLite && hasPricingV2DemandDashboard && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Dashboard,
        Icon: DashboardIcon,
        contentId: ContentId.Dashboard,
      });
    }

    if (isEventEnabledForBoost && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Boost,
        Icon: (props) => (
          <BoostIconDisplay
            {...props}
            isSelected={activeTab === InventoryEventTabs.Boost}
            inactive={
              activeTab !== InventoryEventTabs.Boost &&
              isDarkMode /* we want white in dark mode */
            }
          />
        ),
        contentId: ContentId.Boost,
      });
    }

    if (hasPricingABTestFeature && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.PricingABTest,
        Icon: FlashSaleIcon,
        contentId: ContentId.FlashSale,
      });
    }

    if (
      !isPosLite &&
      (hasFulfillmentRulesEventLevelFeature || event?.viagId != null)
    ) {
      contents.push({
        id: InventoryEventTabs.Fulfillment,
        Icon: DeliveryIcon,
        contentId: ContentId.Delivery,
      });
    }

    if (hasMarketOrdersFeature && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.MarketOrders,
        Icon: ListingIcon,
        contentId: ContentId.MarketOrders,
      });
    }

    if (hasAutoPricingFeature) {
      contents.push({
        id: InventoryEventTabs.SellThroughRate,
        Icon: SellThroughRateIcon,
        contentId: ContentId.SellThroughRate,
      });
    }
    if (!hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Tags,
        Icon: TagNewIcon,
        contentId: ContentId.Tags,
        useStroke: true,
      });
    }
    contents.push({
      id: InventoryEventTabs.Mobile_Sales,
      Icon: SaleIcon,
      contentId: ContentId.Sales,
      mobileOnly: true,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Purchases,
      Icon: PurchaseIcon,
      contentId: ContentId.Purchases,
      mobileOnly: true,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Notes,
      Icon: PlusIcon,
      contentId: ContentId.Notes,
      mobileOnly: true,
    });

    // Add custom event-level tabs
    for (const customTab of customEventTabs) {
      const iconKey = customTab.icon;
      const tabId = `customtab:${customTab.id}` as InventoryEventTabs;
      contents.push({
        id: tabId,
        Icon: (props) => (
          <CustomTabIcon
            iconKey={iconKey}
            size={props.size}
            fill={props.fill}
            className={props.className}
          />
        ),
        contentId: ContentId.Custom,
        labelOverride: customTab.label,
      });
    }

    return contents;
  }, [
    activeTab,
    customEventTabs,
    event?.viagId,
    hasAutoPricingFeature,
    hasConsignorMasterUI,
    hasCustomTabsFeature,
    hasEventDiscoveryInInventoryViewFeature,
    hasFulfillmentRulesEventLevelFeature,
    hasMarketOrdersFeature,
    hasPricingABTestFeature,
    hasPricingV2DemandDashboard,
    isDarkMode,
    isMobileLayout,
    isPosLite,
    isEventEnabledForBoost,
  ]);

  const compListings = useMemo(
    () => compListingsQuery.data ?? [],
    [compListingsQuery.data]
  );

  const contextValue = useMemo<IInventoryEventLocalContext>(
    () => ({
      defaultTab: defaultInventoryTabUserSetting,
      activeTab,
      showMap,
      eventContents,
      marketplaceEventInfo,
      listings,
      compListings,
      toggleShowMap,
      refetchMarketplaceEventInfo,
      setActiveTab: onSetInventoryTab,
      onUpdateDefaultType: setDefaultInventoryTabUserSetting,
    }),
    [
      defaultInventoryTabUserSetting,
      activeTab,
      showMap,
      eventContents,
      marketplaceEventInfo,
      listings,
      compListings,
      toggleShowMap,
      refetchMarketplaceEventInfo,
      onSetInventoryTab,
      setDefaultInventoryTabUserSetting,
    ]
  );

  if (hasContextWithSelectorFeature) {
    return (
      <InventoryEventLocalContextWithSelector.Provider value={contextValue}>
        {children}
      </InventoryEventLocalContextWithSelector.Provider>
    );
  }

  return (
    <InventoryEventLocalContext.Provider value={contextValue}>
      {children}
    </InventoryEventLocalContext.Provider>
  );
};

export const InventoryEventLocalContextProvider = memo(
  InventoryEventLocalContextProviderRaw
);
